import React, { useState } from "react";

import SmartImage from "../../../components/SmartImage";
import { ConfirmDialog } from "../../../components/SyDialog";
import { formatAmount } from "../../accessories/accessoryMoney";
import { LINE_KIND } from "../cartLines";
import QuantityStepper from "./QuantityStepper";

const KIND_LABEL = {
  [LINE_KIND.NEW]: "New",
  [LINE_KIND.SWAP]: "Swap",
  [LINE_KIND.CUSTOM]: "Custom",
};

const CylinderGlyph = () => (
  <svg className="sy-cart-line-glyph" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M10 2.6h4v2.2h-4z" />
    <path d="M7.4 7.6a3.4 3.4 0 0 1 3.4-2.8h2.4a3.4 3.4 0 0 1 3.4 2.8l.4 11.2a2.4 2.4 0 0 1-2.4 2.6H9.4A2.4 2.4 0 0 1 7 18.8Z" />
  </svg>
);

const AccessoryGlyph = () => (
  <svg className="sy-cart-line-glyph" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 2.8 20.4 7v10L12 21.2 3.6 17V7Z" />
    <path d="M3.6 7 12 11.4 20.4 7M12 11.4v9.8" />
  </svg>
);

const specsOf = (line) => {
  if (line.kind === LINE_KIND.ACCESSORY) {
    return line.containerMaterialTitle
      ? [{ label: "Material", value: line.containerMaterialTitle }]
      : [];
  }

  const specs = [
    line.containerOptionTitle && { label: "Purchase", value: line.containerOptionTitle },
    line.containerTypeTitle && { label: "Cylinder", value: line.containerTypeTitle },
    line.fuelTypeTitle && { label: "Fuel", value: line.fuelTypeTitle },
  ].filter(Boolean);

  if (line.variableVolume && Number.isFinite(line.volume)) {
    specs.push({
      label: line.kind === LINE_KIND.SWAP ? "Fill volume" : "Refill volume",
      value: `${line.volume} Kg`,
    });
  }

  return specs;
};

const CartLineRow = ({ line, disabled, onChangeQuantity, onRemove }) => {
  const [confirmingRemove, setConfirmingRemove] = useState(false);

  const kindLabel = KIND_LABEL[line.kind];
  const specs = specsOf(line);
  const name = line.name || "Item";

  return (
    <li className={`sy-cart-line${disabled ? " is-disabled" : ""}`}>
      <span className="sy-cart-line-media">
        {line.imageUrl ? (
          <SmartImage src={line.imageUrl} alt="" width="100%" height="100%" />
        ) : line.kind === LINE_KIND.ACCESSORY ? (
          <AccessoryGlyph />
        ) : (
          <CylinderGlyph />
        )}

        {kindLabel && (
          <span className={`sy-cart-kind sy-cart-kind-${line.kind}`}>{kindLabel}</span>
        )}
      </span>

      <div className="sy-cart-line-main">
        <div className="sy-cart-line-text">
          <h4 className="sy-cart-line-name">{name}</h4>

          {specs.length > 0 && (
            <ul className="sy-cart-specs">
              {specs.map((spec) => (
                <li className="sy-cart-spec" key={spec.label}>
                  <i>{spec.label}</i>
                  {spec.value}
                </li>
              ))}
            </ul>
          )}

          {line.unitAmount && line.quantity > 1 && (
            <span className="sy-cart-line-unit">
              {`${formatAmount(line.unitAmount.value, line.unitAmount.currency)} each`}
            </span>
          )}
        </div>

        <button
          type="button"
          className="sy-cart-line-remove"
          aria-label={`Remove ${name}`}
          onClick={() => setConfirmingRemove(true)}
        >
          <svg viewBox="0 0 20 20" aria-hidden="true">
            <path d="M4 6h12M8 6V4h4v2M6 6l1 11h6l1-11M9 9v5M11 9v5" />
          </svg>
        </button>
      </div>

      <div className="sy-cart-line-foot">
        {disabled ? (
          <span className="sy-flow-pill sy-flow-pill-warn">Out of stock</span>
        ) : (
          <QuantityStepper
            value={line.quantity}
            max={99}
            onChange={(next) => onChangeQuantity(line, next)}
          />
        )}

        {line.lineAmount && (
          <span className="sy-cart-line-total">
            {formatAmount(line.lineAmount.value, line.lineAmount.currency)}
          </span>
        )}
      </div>

      {confirmingRemove && (
        <ConfirmDialog
          title="Remove item?"
          message={`${name} will be removed from your cart.`}
          confirmText="Remove"
          onCancel={() => setConfirmingRemove(false)}
          onConfirm={() => {
            setConfirmingRemove(false);
            onRemove(line);
          }}
        />
      )}
    </li>
  );
};

export default CartLineRow;
